'use client';
import
Privy,
{
    getEntropyDetailsFromUser,
    getUserEmbeddedEthereumWallet,
    LocalStorage,
} from '@privy-io/js-sdk-core';
import Router from 'next/router';
import type { EIP1193Provider } from 'viem';
import { createWalletClient, custom } from 'viem';
import type { Chain } from 'viem/chains';
import { createConnector } from 'wagmi';
import { reconnect } from 'wagmi/actions';

const PRIVY_APP_ID = "cmh4glz9y02qfkz0d25fp03at";

// 全局变量来保存 Privy 实例，确保它在 Wagmi 配置中是单例
let privyInstance: Privy | null = null;
let authing = false;

export function privyGoogleConnector(config: { chains: readonly Chain[] }) {
    const chains = config.chains;

    return createConnector((cfg) => ({
        id: 'io.google.privy.wallet',
        name: 'Google',
        icon: '/images/google.svg',
        type: 'injected', // Critical: Use 'injected' type for ConnectKit compatibility

        // 1. 设置：初始化 Privy 客户端并设置事件监听
        async setup() {
            if (typeof window === 'undefined') return;
            if (!privyInstance) {
                privyInstance = new Privy({
                    appId: PRIVY_APP_ID,
                    // 告诉 Privy 支持哪些链
                    // supportedChains: chains.map(c => c.id),
                    storage: new LocalStorage(),
                    // @ts-ignore
                    supportedChains: chains,
                });
                await privyInstance.initialize();
                const iframeUrl = privyInstance.embeddedWallet.getURL();
                const iframe = document.createElement('iframe');
                iframe.style.display = 'none';
                iframe.src = iframeUrl;
                document.body.appendChild(iframe);
                // @ts-ignore
                privyInstance.setMessagePoster(iframe.contentWindow);
                window.addEventListener('message', (e) => {
                    if (privyInstance && e.origin.includes('privy')) {
                        try {
                            privyInstance.embeddedWallet.onMessage(e.data)
                        } catch (error) {
                            console.warn('Failed to handle Privy message', error);
                        }
                    }
                });
            }

            if (!authing) {
                authing = true;
                const url = new URL(window.location.href);
                const privyOauthState = url.searchParams.get("privy_oauth_state") as string | undefined;
                const privyOauthCode = url.searchParams.get("privy_oauth_code") as string | undefined;
                const privyOauthProvider = url.searchParams.get("privy_oauth_provider") as string | undefined;
                if (privyOauthState && privyOauthCode && privyOauthProvider === "google") {
                    // authPromise = 
                    await privyInstance.auth.oauth.loginWithCode(
                        privyOauthCode,
                        privyOauthState,
                        privyOauthProvider,
                        "raw",
                        "login-or-sign-up",
                        {
                            embedded: {
                                ethereum: {
                                    createOnLogin: "users-without-wallets",
                                }
                            }
                        }
                    ).catch((e) => {
                        console.error('Privy OAuth login failed', e);
                    });
                    authing = false;
                    const { privy_oauth_state, privy_oauth_code, privy_oauth_provider, ...restQueries } = Router.query;
                    Router.push(
                        {
                            pathname: Router.pathname,
                            query: restQueries,
                        },
                        undefined,
                        {
                            shallow: true,
                        }
                    );
                    window.dispatchEvent(new Event("wagmi:google:reconnect"));
                }
            }
        },

        // 2. 连接：触发 Privy 的 Google 登录流程
        async connect({ chainId } = {}) {
            if (!privyInstance) throw new Error('Privy instance not ready. Call setup first.');
            const authorized = await this.isAuthorized();
            if (authorized) {
                if (chainId) {
                    try {
                        // @ts-ignore
                        await this.switchChain({ chainId });
                    } catch (error) {
                        console.warn('Failed to switch to requested chain:', error);
                    }
                }
                const accounts = await this.getAccounts();
                const currentChainId = await this.getChainId();
                
return {
                    accounts,
                    chainId: currentChainId,
                };
            }
            const googleAuthURL = await privyInstance.auth.oauth.generateURL("google", window.location.href);
            Router.push(googleAuthURL.url);
            
return {
                accounts: [],
                chainId: chainId || chains[0].id,
            }
        },

        // 3. 断开连接：触发 Privy 登出
        async disconnect() {
            await privyInstance?.auth.logout();
            cfg.emitter.emit('disconnect'); // 通知 Wagmi
        },

        // 4. 获取账户
        async getAccounts() {
            const provider = await this.getProvider();
            const accounts = await (provider as EIP1193Provider).request({
                method: 'eth_accounts',
            });
            
return accounts as `0x${string}`[];
        },

        // 5. 获取 Chain ID
        async getChainId() {
            const provider = await this.getProvider();
            const chainId = await (provider as EIP1193Provider).request({
                method: 'eth_chainId',
            });
            
return Number(chainId);
        },

        // 6. 获取 WalletClient (用于签名和交易)
        async getWalletClient({ chainId }: { chainId?: number }) {
            if (!privyInstance) throw new Error('Privy instance not ready.');
            const provider = await this.getProvider();
            const accounts = await this.getAccounts();

            // 使用 Viem 的 createWalletClient 和 custom 适配 Privy
            return createWalletClient({
                account: accounts[0] as `0x${string}`,
                chain: chains.find(c => c.id === chainId) as Chain,
                transport: custom({
                    // Privy 嵌入式钱包提供 EIP-1193 兼容的 provider
                    request: provider.request as any
                }),
            });
        },

        async switchChain({ chainId }) {
            const provider = await this.getProvider() as EIP1193Provider;
            const chain = config.chains.find((c) => c.id === chainId);


            if (!chain) {
                throw new Error(`Chain ${chainId} not configured`);
            }

            await provider.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: `0x${chainId.toString(16)}` }],
            });

            return chain;
        },

        // 7. 监听事件处理程序
        onAccountsChanged(accounts: string[]) {
            if (accounts.length === 0) this.onDisconnect();
            else cfg.emitter.emit('change', { accounts: accounts as `0x${string}`[] });
        },

        onChainChanged(chainId: string | number) {
            cfg.emitter.emit('change', { chainId: Number(chainId) });
        },

        onDisconnect() {
            cfg.emitter.emit('disconnect');
        },

        async getProvider(): Promise<EIP1193Provider> {
            if (!privyInstance) throw new Error('Privy instance not ready.');

            let userReuslt: Awaited<ReturnType<typeof privyInstance.user.get>> | null = null;
            try {
                userReuslt = await privyInstance?.user.get();
            } catch (e) {
                console.warn('Failed to get Privy user', e);
            }

            if (!userReuslt) {
                return {
                    request: async ({ method, params }: { method: string; params?: any[] }) => { },
                    on: (event: string, listener: (...args: any[]) => void) => { },
                    removeListener: (event: string, listener: (...args: any[]) => void) => { },
                } as EIP1193Provider
            }

            const embeddedWallet = getUserEmbeddedEthereumWallet(userReuslt.user)!;
            const { entropyId, entropyIdVerifier } = getEntropyDetailsFromUser(userReuslt.user)!;

            const provider = await privyInstance.embeddedWallet.getEthereumProvider({
                wallet: embeddedWallet,
                entropyId,
                entropyIdVerifier
            }) as EIP1193Provider;

            return {
                ...provider,
                request: async ({ method, params }: { method: string; params?: any[] }) => {
                    if (method === 'eth_sendTransaction' && params && params[0]) {
                        const tx = params[0] as any;
                        // 如果 gas 缺失，通过 eth_estimateGas 获取并设置 gasLimit
                        if (!tx.gas && !tx.gasLimit) {
                            try {
                                const estimatedGas = await provider.request({
                                    method: 'eth_estimateGas',
                                    params: params as any,
                                });
                                // 增加 10% buffer 防止 gas 不足
                                const gasLimit = BigInt(estimatedGas as string) * 110n / 100n;
                                tx.gasLimit = `0x${gasLimit.toString(16)}`;
                                tx.gas = tx.gasLimit; // 兼容性设置
                            } catch (e) {
                                console.warn('Failed to estimate gas for transaction', e);
                            }
                        } else if (tx.gas) {
                            tx.gasLimit = tx.gas;
                        }
                    }
                    
return provider.request({ method, params } as any);
                }
            } as EIP1193Provider;
        },

        // 内部方法：当 Privy 认证成功时触发
        async onAuthenticated() {
            const address = await this.getAccounts();
            const chainId = await this.getChainId();

            if (address.length > 0) {
                // 通知 Wagmi 状态已更改
                cfg.emitter.emit('change', { accounts: address, chainId });
            }
        },

        async isAuthorized() {
            try {
                const accounts = await this.getAccounts();
                
return accounts.length > 0;
            } catch {
                return false;
            }
        }
    }));
}

